import { useState, type ReactNode } from "react";
import { NavLink } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import type { StaffRole } from "@rekati/api";
import { BrandLogo, Button, Tooltip } from "@rekati/ui";
import { cn, initials } from "@rekati/utils";
import { spring } from "@rekati/tokens";
import IconScan from "~icons/solar/qr-code-linear";
import IconSearch from "~icons/solar/magnifer-linear";
import IconWalkIn from "~icons/solar/user-plus-rounded-linear";
import IconBoard from "~icons/solar/monitor-smartphone-linear";
import IconOverview from "~icons/solar/chart-square-linear";
import IconAttendees from "~icons/solar/users-group-rounded-linear";
import IconSessions from "~icons/solar/calendar-mark-linear";
import IconDemographics from "~icons/solar/pie-chart-2-linear";
import IconUsers from "~icons/solar/shield-user-linear";
import IconEvent from "~icons/solar/hospital-linear";
import IconActivities from "~icons/solar/widget-4-linear";
import IconContent from "~icons/solar/gallery-wide-linear";
import IconEmail from "~icons/solar/letter-linear";
import IconSurvey from "~icons/solar/clipboard-list-linear";
import IconMenu from "~icons/solar/hamburger-menu-linear";
import IconClose from "~icons/solar/close-circle-linear";
import IconLogout from "~icons/solar/logout-2-linear";
import IconCollapse from "~icons/solar/alt-arrow-left-linear";
import IconExpand from "~icons/solar/alt-arrow-right-linear";
import rekatiMark from "../../assets/brand/rekati-mark.png";
import { publicApi, staffApi } from "../../app/api";
import { areasFor, hasAssistant, type AreaGroup, type AreaId } from "../../app/access";
import { useI18n } from "../../content/i18n";
import { staffSession, useStaffSession } from "../auth/session-store";
import { Kara } from "../kara/Kara";
import { Signature } from "./Signature";

const ICONS: Record<AreaId, typeof IconScan> = {
  scan: IconScan,
  lookup: IconSearch,
  "walk-in": IconWalkIn,
  board: IconBoard,
  overview: IconOverview,
  attendees: IconAttendees,
  sessions: IconSessions,
  demographics: IconDemographics,
  users: IconUsers,
  event: IconEvent,
  activities: IconActivities,
  content: IconContent,
  email: IconEmail,
  survey: IconSurvey,
};

const GROUP_ORDER: AreaGroup[] = ["numbers", "live", "door", "manage"];

/** Role names as this organiser calls them (`GET /v1/brand` roles, decision 80). */
export function useRoleLabel() {
  const roles = useQuery({ queryKey: ["brand"], queryFn: publicApi.brand, staleTime: 30 * 60_000 }).data
    ?.roles;
  return (role: StaffRole) => roles?.[role] || role;
}

const RAIL_KEY = "rekati.console.rail";

/** Whether the desk rail is folded to icons; remembered per browser, open by default. */
function useRailCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === "collapsed";
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((was) => {
      try {
        localStorage.setItem(RAIL_KEY, was ? "expanded" : "collapsed");
      } catch {
        // Storage blocked: the choice lasts this page only.
      }
      return !was;
    });
  return [collapsed, toggle] as const;
}

/**
 * The console's frame: a rail of the areas this role may open, grouped, and
 * an identity card with sign-out at its foot. On a desk the rail folds to
 * icons; on a phone it is a drawer.
 */
export function ConsoleShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const session = useStaffSession();
  const [open, setOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useRailCollapsed();
  if (!session) return null;

  const nav = <Rail role={session.staff.role} onNavigate={() => setOpen(false)} />;

  return (
    <div
      className={cn(
        "min-h-dvh lg:grid motion-safe:lg:transition-[grid-template-columns] motion-safe:lg:duration-200",
        collapsed ? "lg:grid-cols-[72px_1fr]" : "lg:grid-cols-[260px_1fr]",
      )}
    >
      <aside
        id="console-rail"
        className="sticky top-0 hidden h-dvh min-w-0 border-r border-border bg-card lg:block"
      >
        {/* The rail clips its own content as it folds; the toggle outside the clip rides the border. */}
        <div className="h-full overflow-hidden">
          <Rail role={session.staff.role} onNavigate={() => setOpen(false)} collapsed={collapsed} />
        </div>
        <Tooltip content={t(collapsed ? "shell.expand" : "shell.collapse")} side="right">
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={t(collapsed ? "shell.expand" : "shell.collapse")}
            aria-expanded={!collapsed}
            aria-controls="console-rail"
            // On the line between the rail and the page, level with the logo: pt-5 plus half the
            // logo (36px open) or half the mark (44px folded).
            className={cn(
              "absolute -right-[11px] z-40 grid size-[22px] -translate-y-1/2 place-items-center",
              collapsed ? "top-[42px]" : "top-[38px]",
              "rounded-full border border-border bg-card text-muted-foreground shadow-soft transition-colors outline-none hover:bg-secondary hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            {collapsed ? (
              <IconExpand className="size-3.5" aria-hidden="true" />
            ) : (
              <IconCollapse className="size-3.5" aria-hidden="true" />
            )}
          </button>
        </Tooltip>
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/90 px-4 py-3 backdrop-blur lg:hidden">
        <Brand />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpen(true)}
          aria-label={t("shell.menu")}
          aria-expanded={open}
        >
          <IconMenu className="size-6" aria-hidden="true" />
        </Button>
      </header>
      <AnimatePresence>
        {open ? (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <motion.aside
              role="dialog"
              aria-modal="true"
              aria-label={t("shell.menu")}
              className="fixed inset-y-0 left-0 z-50 w-[min(300px,85vw)] border-r border-border bg-card lg:hidden"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={spring.snappy}
              onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
            >
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-3 right-3"
                onClick={() => setOpen(false)}
                aria-label={t("shell.close")}
              >
                <IconClose className="size-6" aria-hidden="true" />
              </Button>
              {nav}
            </motion.aside>
          </>
        ) : null}
      </AnimatePresence>

      <main id="main" className="min-w-0 px-4 pt-6 pb-24 sm:px-8 sm:pt-8">
        {children}
      </main>
      {hasAssistant(session.staff.role) ? <Kara /> : null}
    </div>
  );
}

/** The platform's logo with its partner under it, in the rail and the phone's top bar. */
function Brand() {
  const brand = useQuery({ queryKey: ["brand"], queryFn: publicApi.brand, staleTime: 30 * 60_000 }).data;
  return (
    <div className="grid min-w-0 justify-items-start gap-1">
      {/* The file carries wide margins; this height reads as the console's lead mark. */}
      <BrandLogo
        src={brand?.platform_logo_url}
        name={brand?.platform_name || "Rekati"}
        className="-ml-1 h-9"
        textClassName="text-lg"
      />
      <Signature rail />
    </div>
  );
}

/**
 * The areas this role may open. On a desk the rail can fold to icons
 * (`collapsed`), each named by a tooltip; the phone's drawer is always open.
 */
function Rail({
  role,
  onNavigate,
  collapsed = false,
}: {
  role: StaffRole;
  onNavigate: () => void;
  collapsed?: boolean;
}) {
  const { t } = useI18n();
  const brandName =
    useQuery({ queryKey: ["brand"], queryFn: publicApi.brand, staleTime: 30 * 60_000 }).data?.platform_name ||
    "Rekati";
  const areas = areasFor(role);
  const groups = GROUP_ORDER.map((g) => ({ g, items: areas.filter((a) => a.group === g) })).filter(
    (x) => x.items.length,
  );
  return (
    <div className="flex h-full flex-col">
      {collapsed ? (
        // Folded, the platform keeps its mark: the logo's own symbol, cut from the lockup.
        <div className="flex justify-center px-3 pt-5 pb-4">
          <img src={rekatiMark} alt={brandName} className="size-11" />
        </div>
      ) : (
        <div className="px-5 pt-5 pb-6">
          <Brand />
        </div>
      )}
      <nav aria-label={t("shell.menu")} className="flex-1 overflow-x-hidden overflow-y-auto px-3">
        {groups.map(({ g, items }, gi) => (
          <div key={g} className={collapsed ? "mb-2" : "mb-5"}>
            {collapsed ? (
              // Folded, the group names have no room; a hairline keeps the groups apart.
              gi ? (
                <div className="mx-auto mb-2 h-px w-8 bg-border" aria-hidden="true" />
              ) : null
            ) : (
              <p className="px-2 pb-2 font-mono text-[0.66rem] tracking-[0.16em] text-muted-foreground uppercase">
                {t(`groups.${g}`)}
              </p>
            )}
            <ul role="list" className="grid gap-0.5">
              {items.map((a) => {
                const Icon = ICONS[a.id];
                const name = t(`areas.${a.id}`);
                const link = (
                  <NavLink
                    to={a.path}
                    onClick={onNavigate}
                    aria-label={collapsed ? name : undefined}
                    className={({ isActive }) =>
                      cn(
                        "relative flex h-10 items-center gap-3 rounded-md text-sm font-medium transition-colors",
                        collapsed ? "justify-center" : "px-2.5",
                        isActive
                          ? "bg-secondary text-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive ? (
                          <motion.span
                            layoutId="rail-active"
                            className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-volt"
                            transition={spring.snappy}
                            aria-hidden="true"
                          />
                        ) : null}
                        <Icon className="size-5 shrink-0" aria-hidden="true" />
                        {collapsed ? null : name}
                      </>
                    )}
                  </NavLink>
                );
                return (
                  <li key={a.id}>
                    {collapsed ? (
                      // A plain trigger: NavLink's className is a function, which the tooltip's Slot would flatten.
                      <Tooltip content={name} side="right">
                        <div>{link}</div>
                      </Tooltip>
                    ) : (
                      link
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <IdentityCard collapsed={collapsed} />
    </div>
  );
}

function IdentityCard({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useI18n();
  const session = useStaffSession();
  const roleLabel = useRoleLabel();
  const logout = useMutation({ mutationFn: staffApi.logout, onSettled: () => staffSession.clear() });
  if (!session) return null;
  const { staff } = session;
  return (
    <div
      className={cn(
        "flex items-center border-t border-border",
        collapsed ? "flex-col gap-2 px-2 py-4" : "gap-3 p-4",
      )}
    >
      <span
        className="grid size-9 shrink-0 place-items-center rounded-full bg-volt font-mono text-xs font-semibold text-volt-foreground"
        aria-hidden="true"
        title={collapsed ? staff.full_name || staff.email : undefined}
      >
        {initials(staff.full_name || staff.email)}
      </span>
      {/* Folded, the name and role stay for screen readers only. */}
      <div className={cn("min-w-0 flex-1", collapsed && "sr-only")}>
        <p className="truncate text-sm font-medium" title={t("shell.signedInAs", { name: staff.full_name })}>
          {staff.full_name || staff.email}
        </p>
        <p className="truncate font-mono text-[0.68rem] tracking-wider text-muted-foreground uppercase">
          {roleLabel(staff.role)}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => logout.mutate()}
        loading={logout.isPending}
        aria-label={t("shell.signOut")}
      >
        <IconLogout className="size-5" aria-hidden="true" />
      </Button>
    </div>
  );
}
